import { Link } from "react-router-dom";
import type { Account, Position } from "@/api/types";
import { ExchangeMark } from "@/components/ExchangeMark";

const th = "whitespace-nowrap border-b border-[var(--border)] px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-muted)]";
const td = "whitespace-nowrap border-b border-[var(--divider)] px-3 py-[9px]";

function num(v: unknown, digits = 8): string {
  const n = Number(v);
  if (v == null || v === "" || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { maximumFractionDigits: digits });
}

/** A position, with its account when positions of several accounts share a table. */
export type PositionRow = Position & {
  account?: Account;
  /** The live strategy that trades this symbol on this account, when there is exactly one. */
  strategyHref?: string;
};

type Col = { h: string; right?: boolean; show: boolean; cell: (p: PositionRow) => React.ReactNode; cls?: string };

/** Open positions on the exchange. Columns the exchange doesn't report are left out. */
export function PositionsTable({ positions, empty = "No open positions." }: { positions: PositionRow[]; empty?: string }) {
  if (!positions.length) {
    return <div className="px-4 py-10 text-center text-[13px] text-[var(--text-muted)]">{empty}</div>;
  }
  const has = (k: keyof Position) => positions.some((p) => num(p[k]) !== "" && Number(p[k]) !== 0);
  const all: Col[] = [
    {
      h: "Account",
      show: positions.some((p) => p.account),
      cell: (p) =>
        p.account ? (
          <Link
            to={`/integrations/marketplaces/${p.account.id}`}
            className="inline-flex items-center gap-2 text-[var(--text-secondary)] no-underline hover:underline"
          >
            <ExchangeMark provider={p.account.provider} size={18} />
            {p.account.name}
          </Link>
        ) : null,
    },
    { h: "Symbol", show: true, cls: "font-medium text-[var(--text-primary)]", cell: (p) => p.symbol },
    {
      h: "Side",
      show: true,
      cell: (p) => {
        const short = p.side === "SHORT" || Number(p.size) < 0;
        return <span style={{ color: short ? "var(--danger)" : "var(--success)" }}>{short ? "Short" : "Long"}</span>;
      },
    },
    { h: "Size", right: true, show: true, cell: (p) => num(Math.abs(Number(p.size))) },
    { h: "Entry", right: true, show: true, cls: "text-[var(--text-secondary)]", cell: (p) => num(p.entry_price) },
    { h: "Mark", right: true, show: has("mark_price"), cls: "text-[var(--text-secondary)]", cell: (p) => num(p.mark_price) },
    { h: "Liq. price", right: true, show: has("liquidation_price"), cls: "text-[var(--text-secondary)]", cell: (p) => num(p.liquidation_price) },
    { h: "Leverage", right: true, show: has("leverage"), cell: (p) => (p.leverage ? `${p.leverage}×` : "") },
    {
      h: "Unrealized P&L",
      right: true,
      show: true,
      cell: (p) => {
        const pnl = Number(p.unrealized_pnl);
        return (
          <span style={{ color: !pnl ? "var(--text-muted)" : pnl > 0 ? "var(--success)" : "var(--danger)" }}>
            {pnl > 0 ? "+" : ""}
            {num(p.unrealized_pnl, 2)}
            {p.roe && Number(p.roe) !== 0 ? <span className="ml-1.5 text-xs opacity-80">{`${num(p.roe, 2)}%`}</span> : null}
          </span>
        );
      },
    },
  ];
  all.push({
    h: "",
    right: true,
    show: positions.some((p) => p.strategyHref),
    cell: (p) =>
      p.strategyHref ? (
        <Link to={p.strategyHref} className="btn btn-ghost btn-sm" title="See where the strategy got in, on the chart">
          Chart ›
        </Link>
      ) : null,
  });
  const cols = all.filter((c) => c.show);

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-[13px]">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.h || "actions"} className={`${th} ${c.right ? "text-right" : ""}`}>
                {c.h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {positions.map((p) => (
            <tr key={`${p.account?.id ?? ""}-${p.symbol}-${p.side}`}>
              {cols.map((c) => (
                <td key={c.h || "actions"} className={`${td} ${c.right ? "num text-right" : ""} ${c.cls || ""}`}>
                  {c.cell(p)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

